'use client';

import { MarkdownToReact } from '@ui/components/markdown';
import { Button } from '@ui/components/v2/button';
import { LanguageSelect } from '@ui/components/v2/combobox/languages';
import { MOTIMATE_URL } from 'config';
import NextImage from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { ByMotimate } from '@/components/ByMotimate';
import { Form } from '@/components/Form';
import { Meta } from '@/components/Meta';
import type { Locale } from '@/i18n/config';
import { usePathname, useRouter } from '@/i18n/routing';
import type { Page } from '@/types';

interface Props {
  page: Page;
  locale: Locale;
  heroBlurDataURL?: string;
}

const getTransformedUrl = ({ transformation, url }: { url: string; transformation: string[] }) =>
  `${url.replace('image/upload', `image/upload/${transformation.join(',')}`)}`;

export function LandingPageContent({ page, locale, heroBlurDataURL }: Props) {
  const [isForm, setIsForm] = useState(false);
  const t = useTranslations('general');
  const router = useRouter();
  const pathname = usePathname();

  return (
    <>
      <Meta desc={page.description} hero={page.heroImage} title={page.title} />

      <main className="w-full min-h-screen flex flex-col-reverse lg:flex-row bg-gray-50">
        <LanguageSelect
          containerClassName="absolute z-10 top-5 right-7 lg:left-7 text-left"
          onChange={newLocale => router.push(pathname, { locale: newLocale })}
          value={locale}
        />
        <section className="relative flex-[1_1_50%] sm:p-10 py-8 px-1 m-0 flex flex-col justify-center items-center min-h-[50vh] text-base text-center">
          <div className="m-auto sm:mx-auto sm:mt-0 sm:mb-20 min-w-36 max-w-[25rem] w-full">
            <div className="relative mx-auto h-28 w-72 rounded-lg">
              <NextImage
                priority
                alt={page.title}
                quality={85}
                fill
                src={page.logoImage || '/motimate-logo.png'}
                className="object-contain"
              />
            </div>

            <h1 className="my-4 font-bold text-center text-2xl max-[414px]:text-xl break-words">{page.title}</h1>

            {isForm ? (
              <Form lang={locale} page={page} />
            ) : (
              <>
                <MarkdownToReact desc={page.description} />
                <Button
                  variant="dark"
                  onClick={() => setIsForm(true)}
                  className="mt-4 h-10 px-6 text-sm leading-none rounded-xl"
                >
                  {page.cta || 'Join'}
                </Button>

                <p className="text-gray-600 text-sm mt-4 mx-auto max-w-xs text-center">
                  <a
                    href={`${MOTIMATE_URL}/${page.organizationSlug}/signin`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-800 underline"
                  >
                    {t('alreadySignedUp')}
                  </a>
                </p>
              </>
            )}

            <ByMotimate />
          </div>
        </section>
        <section className="relative flex-[1_1_50%] sm:p-10 py-8 px-1 m-0 flex flex-col justify-center items-center min-h-[50vh] text-base text-center overflow-hidden">
          {page.heroImage ? (
            <NextImage
              alt="heroImage"
              quality={85}
              fill
              src={getTransformedUrl({ url: page.heroImage, transformation: ['w_1280', 'h_1440', 'c_lfill'] })}
              className="object-cover"
              placeholder={heroBlurDataURL ? 'blur' : 'empty'}
              blurDataURL={heroBlurDataURL}
            />
          ) : (
            <NextImage
              alt="heroImage"
              quality={85}
              fill
              priority
              src="/motimate-onboarding.png"
              className="object-cover"
            />
          )}
        </section>
      </main>
    </>
  );
}
